/* @ngInject */
function socialMediaService (CONSTANTS, Utils, ConnectorWebService, $cookies) {
  const BYTES_IN_KILOBYTE = 1024
  const KILOBYTES_IN_MEGABYTE = 1024
  const MAX_SIZE_MB = 8
  const MAX_MATERIALS_COUNT = 20
  const MAX_CAROUSEL_MATERIALS_COUNT = 10

  const service = {
    getGatewayUrl,
    getAccounts,
    publish,
    getContents,
    getContentAsset,
    likePost,
    audit,
    showSocialButton,
    lastUpdate,
    getSocialAccountsExpired,
    getContentToPublish,
    unlinkAccount,
    isMaterialOversized,
    hasOversizedMaterials,
    getOversizedMaterials,
    hasTooManyMaterials,
    validateMaterials,
    getMaterialSizeInMB
  }

  return service

  function getGatewayUrl (socialType) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_SOCIAL_MEDIA_TYPE, [socialType])
    return ConnectorWebService.get(url)
  }

  function getAccounts (socialType, params) {
    let url = Utils.fnReplaceStringParams(CONSTANTS.URL_SOCIAL_MEDIA_ACCOUNTS, [socialType])

    const {
      $order = '',
      $sort = '',
      top = 10,
      skip = 0,
      searchBy = '',
      search = '',
      accountIds = [],
      groupIds = []
    } = params

    if (top || skip) {
      url = Utils.fnAddTopSkipQueriesToUrl(url, top, skip)
    }

    if ($order || $sort) {
      url = Utils.fnAddQueryToUrl(url, { $order, $sort })
    }

    if (accountIds?.length) {
      url = Utils.fnAddQueryToUrl(url, { accountIds })
    }

    if (groupIds?.length) {
      url = Utils.fnAddQueryToUrl(url, { groupIds })
    }

    if (searchBy) {
      url = Utils.fnAddQueryToUrl(url, { searchBy })
    }

    if (search) {
      url = Utils.fnAddQueryToUrl(url, { search })
    }

    return ConnectorWebService.get(url)
  }

  function publish (socialType, payload) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_SOCIAL_MEDIA_PUBLISH, [socialType])
    return ConnectorWebService.post(url, payload)
  }

  function showSocialButton (template, isUser, isEnableSocialMedia, isEditor = false) {
    const isDigital = template?.type === 'DIGITAL' || template?.productType === 'DIGITAL' || template?.product?.type === 'DIGITAL'
    const isNotArchived = !template.isArchived
    const hasNotVideo = !template.hasVideo && !template?.downloadOption?.includes?.('MP4')
    const isAvailable = !template.unavailable
    const layerCount = parseInt(template?.layers?.length) || 1

    const isAbleToShare = isUser && isDigital && isNotArchived && isAvailable && hasNotVideo && isEnableSocialMedia

    if (template.isTemplate && template.isCustomizable) {
      return false
    }

    if (isEditor) {
      const notNeedApproval = !template.needApproval

      return notNeedApproval && hasNotVideo && layerCount <= 10
    }

    if (template.isCustomizable) {
      const isApproved = template.approvalStatus === 'APPROVED'
      const needApproval = template.needApproval

      if (!needApproval) {
        return isAbleToShare && layerCount <= 10
      }

      return isAbleToShare && isApproved && layerCount <= 10
    }

    const unsupportedContentTypes = [
      'avi', 'ktime', 'mpeg', 'x-m4v', 'mp4', 'mov', 'mkv', 'rmvb', 'qt', 'pdf'
    ]

    const assetExtension = template?.asset?.extension
    const extension = assetExtension || template?.extension

    const hasNotUnsupportedExtension = !unsupportedContentTypes.includes(extension)

    return isAbleToShare && hasNotUnsupportedExtension && !template.hasVideo && layerCount <= 10
  }

  function getContents (socialType, top, skip, params) {
    let url = Utils.fnReplaceStringParams(CONSTANTS.URL_SOCIAL_MEDIA_CONTENTS, [socialType])

    const {
      accountIds = [],
      type = [],
      groupIds = [],
      start = '',
      end = '',
      alreadySeen = true,
      nonConformity = undefined
    } = params

    if (top || skip) {
      url = Utils.fnAddTopSkipQueriesToUrl(url, top, skip)
    }

    if (start && end) {
      url = Utils.fnAddStartEndQueriesToUrl(url, { start, end })
    }

    if (accountIds?.length) {
      url = Utils.fnAddQueryToUrl(url, { accountIds })
    }

    if (type?.length) {
      url = Utils.fnAddQueryToUrl(url, { type })
    }

    if (groupIds?.length) {
      url = Utils.fnAddQueryToUrl(url, { groupIds })
    }

    if (typeof alreadySeen !== 'undefined') {
      url = Utils.fnAddQueryToUrl(url, { alreadySeen })
    }

    if (typeof nonConformity !== 'undefined') {
      url = Utils.fnAddQueryToUrl(url, { nonConformity })
    }

    return ConnectorWebService.get(url)
  }

  function getContentAsset (socialType, postId) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_SOCIAL_MEDIA_CONTENTS_ASSET, [socialType, postId])
    return url
  }

  function likePost (postId) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_SOCIAL_MEDIA_POST_LIKE, [postId])
    return ConnectorWebService.post(url)
  }

  function audit (payload) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_SOCIAL_MEDIA_AUDIT)
    return ConnectorWebService.post(url, payload)
  }

  function lastUpdate (socialType) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_SOCIAL_MEDIA_LAST_UPDATE, [socialType])
    return ConnectorWebService.get(url)
  }

  function getSocialAccountsExpired (socialAccounts) {
    const currentDate = new Date()
    return (socialAccounts || []).filter(account => {
      const expirationDate = new Date(account.tokenExpiryDate)
      return expirationDate < currentDate
    })
  }

  function getContentToPublish (socialType, artIds) {
    const artUrl = Utils.fnReplaceStringParams(CONSTANTS.URL_SOCIAL_MEDIA_CONTENT_URL, [socialType])
    let url = artUrl

    const artIdsArray = Array.isArray(artIds) ? artIds : [artIds]
    url = Utils.addQueryStringFromArray(url, 'contents', artIdsArray)

    return ConnectorWebService.get(url)
  }

  function unlinkAccount (accountId) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_SOCIAL_MEDIA_UNLINK_ACCOUNT, [accountId])
    return ConnectorWebService.fnDelete(url)
  }

  function getMaterialSizeInMB (material) {
    const sizeInBytes = material.size || material.fileSize || 0
    return sizeInBytes / (BYTES_IN_KILOBYTE * KILOBYTES_IN_MEGABYTE)
  }

  function isMaterialOversized (material) {
    const sizeInMB = getMaterialSizeInMB(material)
    return sizeInMB > MAX_SIZE_MB
  }

  function hasOversizedMaterials (materials) {
    if (!materials || !materials.length) return false
    return materials.some(material => isMaterialOversized(material))
  }

  function getOversizedMaterials (materials) {
    if (!materials || !materials.length) return []
    return materials.filter(material => isMaterialOversized(material))
  }

  function hasTooManyMaterials (materials, isCarousel = false) {
    if (!materials) return false
    const maxCount = isCarousel ? MAX_CAROUSEL_MATERIALS_COUNT : MAX_MATERIALS_COUNT
    return materials.length >= maxCount
  }

  function validateMaterials (materials, isCarousel = false) {
    const hasTooMany = hasTooManyMaterials(materials, isCarousel)
    const oversizedMaterials = getOversizedMaterials(materials)
    const hasOversized = oversizedMaterials.length > 0

    return {
      hasErrors: hasTooMany || hasOversized,
      canContinue: !hasTooMany && !hasOversized,
      hasTooManyMaterials: hasTooMany,
      hasOversizedMaterials: hasOversized,
      oversizedMaterials
    }
  }
}

export { socialMediaService }
